import { html, TemplateResult } from "lit-html";
import {
  convertDateTime,
  convertDate,
  convertTime,
  renderSplitStringArray,
  convertOverviewFlightState,
  validateBaggageState,
} from "Helper/flightInformationHelper";
import { I18nService } from "Services/I18nService";
import { FlightsInformation } from "Interfaces/flightsInformation";
import { replacePlaceHolder } from "Helper/stringHelper";
import { getDocLocale } from "Helper/metaHelper";

/**
 * Template for new flight info
 *
 * Renders JSON data from API w/ lit-html
 */

export interface airlineContactInformation {
  // e.g. "["+XX (0) XX XX XXX XXX"]"
  phone: string;
  // e.g. ""http://www.airline.com/"
  website: string;
  // e.g. ""Airline Name"
  websiteTitle: string;
}

function isArrivalAndRedirected(data: FlightsInformation) {
  return data.id.startsWith("a") && isRedirected(data);
}

/** ####### HELPERS ####### */

/**
 * Helper to show/hide row in list
 * @param {any} data
 * @returns {string}
 */
function hasData(data: any): string {
  if (data === undefined) {
    return `fra-h-hidden`;
  }
  return ``;
}

/**
 * Helper to check for cancelled status
 * @param {string} status
 * @returns {boolean}
 */
function isCancelled(status: string): boolean {
  return (
    Boolean(status) &&
    (status.toLowerCase() === "annulliert" ||
      status.toLowerCase() === "cancelled" ||
      status.toLocaleLowerCase() === "取消")
  );
}

/**
 * Helper to check for redirected status
 * @param {FlightsInformation} data
 * @returns {boolean}
 */
function isRedirected(data: FlightsInformation): boolean {
  // available when flights are diverted
  return Boolean(data?.div);
}

/**
 * Helper to check if flight is in the air and tracking is therefore available
 * @param {FlightsInformation} data
 * @returns {boolean}
 */
function isCurrentlyFlying(data: FlightsInformation): boolean {
  let currentlyFlying = false;
  if (data.schedArr) {
    if (data.esti) {
      const newArrival =
        new Date(data.esti).getTime() + (new Date(data.schedArr).getTime() - new Date(data.sched).getTime());
      currentlyFlying = new Date(data.esti).getTime() <= new Date().getTime() && new Date().getTime() <= newArrival;
    } else {
      currentlyFlying =
        new Date(data.sched).getTime() <= new Date().getTime() &&
        new Date().getTime() <= new Date(data.schedArr).getTime();
    }
  } else if (data.schedDep) {
    if (data.esti) {
      currentlyFlying =
        new Date(data.schedDep).getTime() <= new Date().getTime() &&
        new Date().getTime() <= new Date(data.esti).getTime();
    } else {
      currentlyFlying =
        new Date(data.schedDep).getTime() <= new Date().getTime() &&
        new Date().getTime() <= new Date(data.sched).getTime();
    }
  }
  return currentlyFlying;
}

/*
 * Helper to check for Train status
 */
export function isTrain(ac: string): boolean {
  return Boolean(ac) && ac === "TRS";
}

/*
 * Helper to check for departure status
 */
function isDeparture(id: string): boolean {
  return id.startsWith("d");
}

/** ####### RENDERER ####### */
/**
 * Render code shares associated w/ status
 * @param {Array|undefined} arr
 * @returns {TemplateResult}
 */
function renderCodes(arr?: string[]): TemplateResult {
  if (!arr) {
    return html`
      &mdash;
    `;
  }

  const codesArr: string[] = renderSplitStringArray(arr);
  if (codesArr.length > 1) {
    const codes = codesArr.length;
    return html`
      <ul
        class="fra-m-flight-info__airline-codeshare-slides"
        style="--items: ${Number(codes)}; 
        -webkit-animation-duration: ${Number(codes * 3)}s;
        -webkit-animation-timing-function: steps(${Number(codes)});
        animation-duration: ${Number(codes * 3)}s;
        animation-timing-function: steps(${Number(codes)});"
      >
        ${codesArr.map(
          (code: string) => html`
            <li class="fra-m-flight-info__airline-codeshare__slide-up">${code}</li>
          `,
        )}
        <li class="fra-m-flight-info__airline-codeshare__slide-up">${codesArr[0]}</li>
      </ul>
    `;
  }
  return html`
    ${codesArr[0]}
  `;
}

/**
 * Render status, create coloured output
 * @param {number} flstatus
 * @param {string} status
 * @param {string[]} bag
 * @returns {TemplateResult}
 */
function renderStatus(flstatus: number, status: string, bag: string[]): TemplateResult {
  if (status) {
    return html`
      <dd class="fra-e-list__desc fra-m-flight-info__flight-status-${convertOverviewFlightState(flstatus, status)}">
        ${validateBaggageState(status)
          ? html`
              <p class="fra-m-flight-info__flight-status">
                <fra-e-icon class="fra-e-icon" icon-id="fra-icon-gepaeck-klein"></fra-e-icon>
                <span class="fra-m-flight-info__flight-status-content">${status}</span>
              </p>
              ${bag
                ? html`
                    <p class="fra-m-flight-info__flight-status-bag">
                      ${replacePlaceHolder(
                        I18nService.getInstance().getKey(
                          "fraport.travel.components.content.flight.table.row.status.band.label",
                        ),
                        bag.join(", "),
                      )}
                    </p>
                  `
                : html``}
            `
          : html`
              <p class="fra-m-flight-info__flight-status">
                <span class="fra-m-flight-info__flight-status-indicator"></span>
                <span class="fra-m-flight-info__flight-status-content">${status}</span>
              </p>
            `}
      </dd>
    `;
  }

  return html``;
}

/**
 * Render times associated w/ this flight (planned and delayed)
 * @param {string} sched
 * @param {string | undefined} esti
 * @returns {TemplateResult}
 */
function renderTime(data: FlightsInformation): TemplateResult {
  const sched = data.sched;
  const esti = data?.esti;

  if (!sched) return html``;

  if (isArrivalAndRedirected(data)) {
    return html`
      <span class="fra-m-flight-info__redirected">
        <time value="${sched}">${convertTime(sched)}</time>
      </span>
    `;
  }

  if (esti && esti !== sched) {
    return html`
      <span class="fra-m-flight-info__time-delayed">
        <time value="${sched}">${convertTime(sched)}</time>
      </span>
      <span class="fra-m-flight-info__updated-time">
        <time value="${esti}">${convertTime(esti)}</time>
      </span>
    `;
  }
  return html`
    <time value="${sched}">${convertTime(sched)}</time>
  `;
}

/**
 * Render times associated w/ this flight (planned and delayed)
 * @param {FlightsInformation} data
 * @param {locale} string
 * @returns {TemplateResult}
 */
function renderDate(data: FlightsInformation, locale: string): TemplateResult {
  const dateTemplate = html`
    ${convertDate(data.sched, locale, { month: "2-digit", day: "numeric", year: "2-digit" })}
  `;

  if (isArrivalAndRedirected(data)) {
    return html`
      ${dateTemplate}
    `;
  }

  const schedDate = convertDate(data.sched, locale, { month: "2-digit", day: "numeric", year: "2-digit" });
  const estiDate = data.esti
    ? convertDate(data.esti, locale, { month: "2-digit", day: "numeric", year: "2-digit" })
    : "";

  if (schedDate && estiDate && schedDate !== estiDate) {
    return html`
      <span class="fra-m-flight-info__old">${schedDate}</span>
      <span class="fra-m-flight-info__updated">${estiDate}</span>
    `;
  }

  return dateTemplate;
}

/**
 * Render terminal associated w/ this flight (planned and delayed)
 * @param {FlightsInformation} data
 * @returns {TemplateResult}
 */
function renderTerminal(data: FlightsInformation): TemplateResult {
  if (!data.terminal || isCancelled(data.status) || isArrivalAndRedirected(data)) {
    return html`
      &mdash;
    `;
  }

  if (data.terminal && data.oldterminal) {
    return html`
      <div class="fra-m-flights-info__terminal-redirected">
        <div class="fra-m-flight-info__old">${data.oldterminal}</div>
        <div class="fra-m-flight-info__updated">${data.terminal}</div>
      </div>
    `;
  }

  return html`
    <div class="fra-m-flights__td-terminal-content">
      <span class="fra-m-flights__td-terminal-content-text">${data.terminal}</span>
    </div>
  `;
}

/**
 * Render hall associated w/ Halle
 * @param {FlightsInformation} data
 * @param {Boolean} isArrival
 * @returns {TemplateResult}
 */
function renderCheckIn(data: FlightsInformation, isArrival = false): TemplateResult {
  if (!data.halle || isCancelled(data.status) || isArrivalAndRedirected(data)) {
    return html`
      &mdash;
    `;
  }

  let halle = data.halle;
  if (data.schalter) {
    halle += `, ${data.schalter}`;
  }

  let label = "{0}";

  // on arrival page label is added
  if (isArrival) {
    label = I18nService.getInstance().getKey(
      "fraport.travel.components.content.flight.table.row.location.checkin.arrival",
    );
  }

  if (data.oldhall && data.halle) {
    return html`
      <div class="fra-m-flight-info__old">${replacePlaceHolder(label, data.oldhall)}</div>
      <div class="fra-m-flight-info__updated">${replacePlaceHolder(label, halle)}</div>
    `;
  }

  return html`
    ${replacePlaceHolder(label, halle)}
  `;
}

/**
 * Render gate or platform information if available
 * @param {FlightsInformation} data
 * @returns {TemplateResult}
 */
function renderGate(data: FlightsInformation): TemplateResult {
  // removing T in front of track number
  if (isTrain(data.ac)) {
    if (data.pos) data.pos = data.pos.substring(1);
    if (data.gate) data.gate = data.gate.substring(1);
    if (data.oldgate) data.oldgate = data.oldgate.substring(1);
  }
  const infoLabel = data.id.startsWith("a")
    ? "fraport.travel.components.content.flight.table.exit"
    : "fraport.travel.components.content.flight.table.gate";
  const infoData = isDeparture(data.id) ? data.gate : isTrain(data.ac) ? data.pos : data.ausgang;
  const isUpdatedGate = data.oldgate && data.gate;
  let label = "{0}";

  // on arrival flight additional label is used
  if (data.id.startsWith("a") && !isTrain(data.ac)) {
    label = I18nService.getInstance().getKey(
      "fraport.travel.components.content.flight.table.row.location.checkin.arrival",
    );
  }

  return html`
    <div class="fra-m-flight-info__fact-entry">
      <dt class="fra-m-flight-info__fact-label">
        ${I18nService.getInstance().getKey(
          isTrain(data.ac) ? "fraport.travel.components.content.flight.table.platform" : infoLabel,
        )}
      </dt>
      <dd class="fra-m-flight-info__fact-data">
        ${!infoData || isCancelled(data.status) || isArrivalAndRedirected(data)
          ? html`
              &mdash;
            `
          : isUpdatedGate
            ? html`
                <div class="fra-m-flight-info__old">${replacePlaceHolder(label, data.oldgate)}</div>
                <div class="fra-m-flight-info__updated">${replacePlaceHolder(label, data.gate)}</div>
              `
            : replacePlaceHolder(label, infoData)}
      </dd>
    </div>
  `;
}

function renderFlightAware(flightAwareUrl: string, data: FlightsInformation, locale: string): TemplateResult {
  if (flightAwareUrl !== "") {
    return html`
      <div class="fra-m-flight-info__flight-aware">
        <div class="fra-m-iframe">
          <iframe src="${flightAwareUrl}"></iframe>
        </div>
      </div>
      <p class="fra-p--small fra-m-flight-info__update-footnote">
        ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.footnote")}
        ${convertDateTime(data.lu, locale, { month: "2-digit", day: "numeric", year: "numeric" })}
      </p>
    `;
  }

  return html``;
}

function renderFlightAwareNotAvailable(): TemplateResult {
  return html`
    <p class="fra-p fra-m-flight-info__flight-aware--not-available">
      ${I18nService.getInstance().getKey(
        "fraport.travel.components.content.flight.table.flightaware.notAvailable.label",
      )}
    </p>
  `;
}

/** ####### EXPORTS ####### */

/**
 * Renders flight-detail-list
 *
 * @param {FlightsInformation} data
 * @param {string} airlineUrl
 * @param {string} airlineDelayUrl
 * @param {string} locale
 * @returns {TemplateResult}
 */
/* eslint-disable max-params */
export const flightsDetailTemplate = (
  data: FlightsInformation,
  airlineUrl: string,
  airlineDelayUrl: string,
  flightAwareUrl: string,
  locale: string,
  siteRootPath: string,
): TemplateResult => {
  return html`
    <fra-e-list class="fra-e-list">
      <div class="fra-m-module-heading">
        <div class="fra-grid">
          <div class="fra-grid-row">
            <div class="fra-grid-col-mq1-12 fra-grid-col-mq3-10 fra-grid-col-mq5-8 fra-grid-offset-mq5-1">
              <div class="fra-m-flight-info__airline">
                <h1 class="fra-h1 fra-m-flight-info__airline-header">${data.fnr}</h1>
                <img
                  class="fra-icon fra-m-flight-info__airline-icon"
                  src="${siteRootPath}/_jcr_content.airlineIcon.${data.al}.jpg/${data.al}"
                  alt="${data.alname}"
                />
              </div>
              <p class="fra-h1 fra-m-flight-info__airline-header">
                ${I18nService.getInstance().getKey(
                  `fraport.travel.components.content.flight.table.apname.${
                    isDeparture(data.id) ? "to" : "from"
                  }.prefix`,
                )}
                ${data.apname}
              </p>

              ${data.cs
                ? html`
                  <div class="fra-m-flight-info__airline-codeshare">
                    <span class="fra-m-flight-info__fact-label">
                      ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.codeshare")}:
                    </span>
                    <div class="fra-m-flight-info__fact-label fra-m-flight-info__airline-codeshare-content ${hasData(
                      data.cs,
                    )}">${renderCodes(data.cs)}</span>
                  </div>
                `
                : html``}
            </div>
          </div>
        </div>
      </div>
      <div class="fra-grid">
        <div class="fra-grid-row">
          <div class="fra-grid-col-mq1-12 fra-grid-col-mq3-10 fra-grid-col-mq5-8 fra-grid-offset-mq5-1">
            <div class="fra-m-flight-info__airline-status">${renderStatus(data.flstatus, data.status, data.bag)}</div>
          </div>
        </div>
        <div class="fra-grid-row">
          <div class="fra-grid-col-mq1-12 fra-grid-col-mq3-10 fra-grid-col-mq5-8 fra-grid-offset-mq5-1">
            <dl class="fra-m-flight-info__fact-list fra-m-flight-info__fact-details fra-h-cols-auto">
              <div class="fra-m-flight-info__fact-entry">
                <dt class="fra-m-flight-info__fact-label">
                  ${isTrain(data.ac)
                    ? I18nService.getInstance().getKey(
                        isDeparture(data.id)
                          ? "fraport.travel.components.trainDepartures.table.heading.departure"
                          : "fraport.travel.components.trainDepartures.table.heading.arrival",
                      )
                    : I18nService.getInstance().getKey(
                        isDeparture(data.id)
                          ? "fraport.travel.components.content.flight.table.time.departures.label"
                          : "fraport.travel.components.content.flight.table.time.arrivals.label",
                      )}
                </dt>
                <dd
                  class="fra-m-flight-info__fact-data ${isCancelled(data.status) ? "fra-m-flight-info__cancelled" : ""}"
                >
                  ${renderTime(data)}
                </dd>
              </div>
              <div class="fra-m-flight-info__fact-entry">
                <dt class="fra-m-flight-info__fact-label">
                  ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.day.label")}
                </dt>
                <dd
                  class="fra-m-flight-info__fact-data ${hasData(data.sched)} ${isCancelled(data.status)
                    ? "fra-m-flight-info__cancelled"
                    : ""}"
                >
                  ${renderDate(data, locale)}
                </dd>
              </div>
              <div class="fra-m-flight-info__fact-entry">
                <dt class="fra-m-flight-info__fact-label">
                  ${isTrain(data.ac)
                    ? I18nService.getInstance().getKey(
                        "fraport.travel.components.trainDepartures.table.heading.trainStation",
                      )
                    : I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.terminal")}
                </dt>
                <dd class="fra-m-flight-info__fact-data">
                  ${isTrain(data.ac)
                    ? I18nService.getInstance().getKey(
                        "fraport.travel.components.trainDepartures.table.option.fernBahnhof",
                      )
                    : renderTerminal(data)}
                </dd>
              </div>
              <div
                class="fra-m-flight-info__fact-entry ${data.id.startsWith("a") || isTrain(data.ac)
                  ? "fra-state-hidden"
                  : ""}"
              >
                <dt class="fra-m-flight-info__fact-label">
                  ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.checkin")}
                </dt>
                <dd class="fra-m-flight-info__fact-data">${renderCheckIn(data)}</dd>
              </div>
              ${renderGate(data)}

              <div class="fra-m-flight-info__fact-entry fra-m-flight-info__fact-airline-link">
                <dt class="fra-m-flight-info__fact-label">
                  ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.airline")}
                </dt>
                <dd class="fra-m-flight-info__fact-data ${hasData(data.alname)}">
                  <a
                    data-gaTracking='{"event":"gaEvent","eventCategory":"FlightInfo","eventAction":"airline click", "eventLabel": "${airlineUrl}"}'
                    class="fra-e-link fra-e-link--with-icon fra-e-link--animated-right"
                    href="${airlineUrl}"
                    target="_blank"
                  >
                    <span class="fra-e-link__label">${data.alname}</span>
                    <fra-e-icon class="fra-e-icon " icon-id="fra-icon-arrow-right"></fra-e-icon>
                  </a>
                </dd>
              </div>
              <div class="fra-grid-row fra-m-flight-info__footnote">
                <div class="fra-grid-col-mq1-12">
                  <p class="fra-p--small">
                    ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.info.table.footnote")}
                    ${convertDateTime(data.lu, locale, { month: "2-digit", day: "numeric", year: "numeric" })}
                  </p>
                </div>
              </div>
              <div
                class="fra-m-flight-info__more-info ${isCancelled(data.status) ? "fra-state-hide" : ""} ${isTrain(
                  data.ac,
                )
                  ? "fra-m-flight-info__more-info-has-train"
                  : ""}"
              >
                ${isTrain(data.ac)
                  ? html`
                      <button
                        class="fra-e-button fra-e-button--bright fra-e-button--icon-left fra-m-flight-info__more-info-updates"
                        data-gaTracking='{"event":"gaEvent","eventCategory":"FlightInfo","eventAction":"click", "eventLabel": "train updates"}'
                        onclick="document.querySelector('.fra-js-flight-info__train-updates-overlay').open()"
                      >
                        <span class="fra-e-button__label">
                          ${I18nService.getInstance().getKey(
                            "fraport.travel.components.content.flight.table.train.updates.label",
                          )}
                        </span>
                        <fra-e-icon class="fra-e-icon fra-e-button__icon" icon-id="fra-icon-updates-klein"></fra-e-icon>
                      </button>
                    `
                  : html`
                      <button
                        class="fra-e-button fra-e-button--bright fra-e-button--icon-left fra-m-flight-info__more-info-updates"
                        data-gaTracking='{"event":"gaEvent","eventCategory":"FlightInfo","eventAction":"click", "eventLabel": "flight updates"}'
                        onclick="document.querySelector('.fra-js-flight-info__flight-updates-overlay').open()"
                      >
                        <span class="fra-e-button__label">
                          ${I18nService.getInstance().getKey(
                            "fraport.travel.components.content.flight.table.flight.updates.label",
                          )}
                        </span>
                        <fra-e-icon class="fra-e-icon fra-e-button__icon" icon-id="fra-icon-updates-klein"></fra-e-icon>
                      </button>
                      ${renderFlightOverviewLink(data.fnr)}
                    `}
              </div>
            </dl>
          </div>
        </div>
        ${renderInfoBox(data, airlineUrl)}
      </div>
    </fra-e-list>
  `;
};

function renderInfoBox(data: FlightsInformation, airlineUrl: string) {
  if (isCancelled(data.status) || isRedirected(data)) {
    const infoBoxHeader = `${I18nService.getInstance().getKey(
      isCancelled(data.status)
        ? `fraport.travel.components.content.${isTrain(data.ac) ? "train" : "flight"}.table.infobox.header.cancel.label`
        : `fraport.travel.components.content.${
            isTrain(data.ac) ? "train" : "flight"
          }.table.infobox.header.reroute.label`,
    )}`;

    let infoBoxContent = `${I18nService.getInstance().getKey(
      isCancelled(data.status)
        ? `fraport.travel.components.content.${
            isTrain(data.ac) ? "train" : "flight"
          }.table.infobox.content.cancel.label`
        : `fraport.travel.components.content.${
            isTrain(data.ac) ? "train" : "flight"
          }.table.infobox.content.reroute.label`,
    )}`;

    if (isRedirected(data) && data.divname) {
      infoBoxContent = replacePlaceHolder(infoBoxContent, data.divname);
    }

    return html`
      <div class="fra-grid-row fra-m-flight-info__info-box">
        <div class="fra-grid-col-mq1-12 fra-grid-col-mq5-10 fra-grid-offset-mq5-1">
          <div class="fra-m-component fra-m-offer-teaser fra-m-offer-teaser-bg--light-blue">
            <div class="fra-m-offer-teaser__text">
              <h2 class="fra-h2 fra-m-offer-teaser__text-header">${infoBoxHeader}</h2>
              <h4 class="fra-h6">${infoBoxContent}</h4>
            </div>
            <div class="fra-m-offer-teaser__button">
              ${isTrain(data.ac) ? renderDBLink(data) : renderAirlineLink(data, airlineUrl)}
            </div>
          </div>
        </div>
      </div>
    `;
  }
  return ``;
}

function renderFlightOverviewLink(flightNumber: string) {
  const canonicalLinkElement = document.querySelector('link[rel="canonical"]');
  if (canonicalLinkElement) {
    const link = canonicalLinkElement.getAttribute("href");
    return html`
      <a class="fra-e-link fra-e-link--with-icon fra-e-link--animated-right" href="${link}" target="_self">
        <span class="fra-e-link__label">
          ${replacePlaceHolder(
            I18nService.getInstance().getKey("fraport.travel.components.content.flight.info.link.label"),
            flightNumber,
          )}
        </span>
        <fra-e-icon class="fra-e-icon" icon-id="fra-icon-arrow-right"></fra-e-icon>
      </a>
    `;
  }
  return html``;
}

function renderAirlineLink(data: FlightsInformation, airlineUrl: string): TemplateResult {
  return html`
    <a class="fra-e-button fra-e-button--bright" href="${airlineUrl}" target="_self">
      <span class="fra-e-button__label">
        ${replacePlaceHolder(
          I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.infobox.button.label"),
          data.alname,
        )}
      </span>
    </a>
  `;
}

function renderDBLink(data: FlightsInformation): TemplateResult {
  const schedDate = new Date(data.sched);
  const date = `${schedDate.getFullYear()}-${schedDate.toLocaleDateString("en-US", {
    month: "2-digit",
  })}-${schedDate.toLocaleDateString("en-US", {
    day: "2-digit",
  })}`;
  const apname = data.apname ? data.apname : "Frankfurt(M) Flughafen Fernbf";
  const rouname = data.rouname ? data.rouname : "Frankfurt(M) Flughafen Fernbf";
  const toStationId = isDeparture(data.id) ? apname : rouname;
  const fromStationId = isDeparture(data.id) ? rouname : apname;
  const time = encodeURIComponent(convertTime(data.sched));
  let langUrl = "www.bahn.de";
  if (getDocLocale() !== "de-DE") {
    langUrl = "int.bahn.de/en";
  }
  const url = `https://${langUrl}/buchung/start#sts=false&so=${fromStationId}%20${toStationId}&hd=${date}T${time}&hza=D`;

  return html`
    <a class="fra-e-button fra-e-button--bright" href="${url}" target="_blank">
      <span class="fra-e-button__label">
        ${I18nService.getInstance().getKey("fraport.travel.components.content.train.table.infobox.button.label")}
      </span>
    </a>
  `;
}

/**
 * Renders Updates-Overlay-Template for flights or trains
 * @param {FlightsInformation} data
 * @returns {TemplateResult}
 */
export const updatesOverlayTemplate = (data: FlightsInformation): TemplateResult => {
  return html`
    <div class="fra-grid">
      <div class="fra-grid-row">
        <div class="fra-grid-col-mq1-12">
          <p class="fra-p--small fra-m-flight-info__overlay-subheader">
            ${data.fnr}
            ${I18nService.getInstance().getKey(
              `fraport.travel.components.content.flight.table.apname.${isDeparture(data.id) ? "to" : "from"}.prefix`,
            )}
            ${data.apname}
          </p>
          <h2 class="fra-h2 fra-m-flight-info__overlay-header">
            ${I18nService.getInstance().getKey(
              `fraport.travel.components.content.${isTrain(data.ac) ? "train" : "flight"}.updates.overlay.header.label`,
            )}
          </h2>
        </div>
      </div>
    </div>
  `;
};
